import {formatDuration, zeroPad} from "@/tools";
import {Bin} from "d3-array";
import {Column} from "exceljs";
import {saveAs} from "file-saver";
import {Call, QueueCall} from "./types";
import {IntlShape, MessageDescriptor} from "react-intl";
import {Entry} from "./AgentStateSummary";
import {RecordingStore} from "@/api";

/// returns the call setup time in seconds
function callSetupTime(call: Call): number {
    const start = new Date(call.callTime);
    const conn = new Date(call.connectTime);
    return (conn.valueOf() - start.valueOf()) / 1000;
}

function queueTalkTime(call: QueueCall): number {
    // sum of connected time of agents
    return call.agent.reduce((s, c) => s + c.duration, 0);
}

function queueWaitTime(call: QueueCall): number {
    const start = new Date(call.received.connectTime);
    const end = call.agent.length ? new Date(call.agent[0].callTime) : call.received.disconnectTime;
    return (end.valueOf() - start.valueOf()) / 1000;
}

function queueSpeedOfAnswer(call: QueueCall): number {
    // average of setup times time of agents
    return call.agent.reduce((s, c) => s + callSetupTime(c), 0) / call.agent.length;
}

export function averageCallSetupTime(calls: Call[]): number {
    return calls.length ? calls.reduce((s, c) => s + callSetupTime(c), 0) / calls.length : 0.0;
}

/*
export function averageCallSetupTime(calls: QueueCall[]): number {
    return calls.length ? calls.reduce((s, c) => s + callSetupTime(c.received), 0) / calls.length : 0.0;
}
*/

export function averageWaitTime(calls: QueueCall[]): number {
    return calls.length ? calls.reduce((s, c) => s + queueWaitTime(c), 0) / calls.length : 0.0;
}

export function averageTalkTime(calls: QueueCall[]): number {
    return calls.length ? calls.reduce((s, c) => s + queueTalkTime(c), 0) / calls.length : 0.0;
}

export function averageSpeedOfAnswer(calls: QueueCall[]): number {
    return calls.length ? calls.reduce((s, c) => s + queueSpeedOfAnswer(c), 0) / calls.length : 0.0;
}

export function averageCallDuration(calls: Call[]): number {
    return calls.length ? calls.reduce((s, c) => s + c.duration, 0) / calls.length : 0.0;
}

export function sumCallDuration(calls: Call[]): number {
    return calls.reduce((s, c) => s + c.duration, 0);
}

// Format date as a basic label for filename
function format_today_date_for_file(): string {
    const today = new Date();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const hour = today.getHours();
    const minute = today.getMinutes();
    return (
        today.getFullYear() + "-" + zeroPad(month) + "-" + zeroPad(day) + "-" + zeroPad(hour) + "-" + zeroPad(minute)
    );
}

// For keyword arguments
type GraphDataDownloadArguments = {
    // Mappings to be used for columns names and their filtered values
    mappings: Record<string, any>;
    // Calls and dates
    binnedCalls: Bin<QueueCall, Date>[];
    // Translation, can not use hooks here
    intl: IntlShape;
    // Sheet name, gets translated in function
    sheetName: MessageDescriptor;
    // File name, no translation
    fileName: string;
    // Whether to put the date in the file name
    includeDate?: boolean;
    // Whether to show 2 decimal places
    decimalPlaces?: number;
};

// Function to download graph data as an excel file
export const graph_data_download_in_excel = async ({
    mappings,
    binnedCalls,
    intl,
    sheetName,
    fileName,
    includeDate = true,
    decimalPlaces = 0,
}: GraphDataDownloadArguments): Promise<void> => {
    const {Workbook} = await import(/* webpackChunkName: "exceljs" */ "exceljs");
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet(intl.formatMessage(sheetName));
    // For more convinient way to use keys
    const mappingNames = Object.keys(mappings);

    // Set the styling for the header
    const sheetHeader = sheet.getRow(1);
    sheetHeader.alignment = {vertical: "middle", horizontal: "center", wrapText: true};
    sheetHeader.height = 30;

    // First column is fixed to date and time
    const columns = [
        {
            header: intl.formatMessage({id: "call_queue.statistics.graph.time", defaultMessage: "Time"}),
            key: "time",
            width: 25,
        } as Partial<Column>,
    ];

    // Setting up the columns based on the mappings
    mappingNames.map((name) => {
        columns.push({
            header: name.charAt(0).toUpperCase() + name.slice(1),
            key: name,
            width: 25,
        });
    });

    // This is a workaround for the issue with adding
    // the columns after setting this initial value (this is how exceljs works)
    sheet.columns = columns;

    binnedCalls.map((bin) => {
        const row = {} as any;
        // For some reason, it is always using UTC+0 timezone,
        // even though the data is in our zone,
        // so needed to add the timezone offset relative to the user
        bin.x0.setMinutes(bin.x0.getMinutes() - bin.x0.getTimezoneOffset());
        row["time"] = bin.x0;

        mappingNames.map((name) => {
            const value = mappings[name](bin);
            row[name] = value;
        });
        const addedRow = sheet.addRow(row);

        // Formatting can be done only after adding the row, exceljs just works this way
        if (decimalPlaces) addedRow.numFmt = `0.${"0".repeat(decimalPlaces)}`;

        // Jul 26, 2022, 08:00
        addedRow.getCell(1).numFmt = "MMM D, YYYY HH:MM";
    });

    // Construct the file name based on the settings
    const getFileName = (): string => {
        // Translate file name if needed
        const fn = fileName;
        if (includeDate) return fn + "-" + format_today_date_for_file() + ".xlsx";
        return fn + ".xlsx";
    };

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], {type: "application/octet-stream"}), getFileName());
};

// Function to download agent table data as an excel file
export const download_excel_agent_table = async (entries: Entry[], intl: IntlShape): Promise<void> => {
    const {Workbook} = await import(/* webpackChunkName: "exceljs" */ "exceljs");
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet("Agent Table");

    // Setting columns manually, as this is a special case
    sheet.columns = [
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.agent",
                defaultMessage: "Agent",
            }),
            key: "agent",
            width: 20,
        },
        {
            header: intl.formatMessage({id: "call_queue.statistics.agent-report.fields.day", defaultMessage: "Day"}),
            key: "day",
            width: 20,
        },
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.loggedIn",
                defaultMessage: "Logged in",
            }),
            key: "loggedIn",
            width: 20,
        }, // also "calls"
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.afterCallWork",
                defaultMessage: "ACW",
            }),
            key: "afterCallWork",
            width: 20,
        }, // also "callTime"
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.onThePhone",
                defaultMessage: "On the phone",
            }),
            key: "onThePhone",
            width: 20,
        }, // also "avgSetupTime"
        {
            header: intl.formatMessage({id: "call_queue.statistics.agent-report.fields.busy", defaultMessage: "Busy"}),
            key: "busy",
            width: 20,
        },
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.ccAux1",
                defaultMessage: "AUX1",
            }),
            key: "aux1",
            width: 20,
        },
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.ccAux2",
                defaultMessage: "AUX2",
            }),
            key: "aux2",
            width: 20,
        },
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.ccAux3",
                defaultMessage: "AUX3",
            }),
            key: "aux3",
            width: 20,
        },
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.calls",
                defaultMessage: "Call #",
            }),
            key: "calls",
            width: 20,
        },
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.callTime",
                defaultMessage: "In call",
            }),
            key: "callTime",
            width: 20,
        },
        {
            header: intl.formatMessage({
                id: "call_queue.statistics.agent-report.fields.avgSetupTime",
                defaultMessage: "Setup time",
            }),
            key: "avgSetupTime",
            width: 20,
        },
    ];

    // Setting header style
    const sheetHeader = sheet.getRow(1);
    sheetHeader.alignment = {horizontal: "center", vertical: "middle"};
    sheetHeader.height = 25;

    entries.forEach((entry) => {
        const row1 = {} as Record<string, any>;
        row1["agent"] = entry.agent;

        // For some reason, it is always using UTC+0 timezone
        // so needed to add the timezone offset relative to the user
        const day = new Date(entry.day);
        day.setMinutes(entry.day.getMinutes() - entry.day.getTimezoneOffset());

        row1["day"] = day;
        row1["loggedIn"] = Math.floor(entry.data.loggedIn / 1000);
        row1["afterCallWork"] = Math.floor(entry.data.afterCallWork / 1000);
        row1["onThePhone"] = Math.floor(entry.data.onThePhone / 1000);
        row1["busy"] = Math.floor(entry.data.busy / 1000);
        row1["aux1"] = Math.floor(entry.data.ccAux1 / 1000);
        row1["aux2"] = Math.floor(entry.data.ccAux2 / 1000);
        row1["aux3"] = Math.floor(entry.data.ccAux3 / 1000);
        row1["calls"] = entry.calls.queueCalls;
        row1["callTime"] = Math.round(entry.calls.queueCallTime);
        row1["avgSetupTime"] = Math.round(entry.calls.avgSetupTime);
        const addedRow = sheet.addRow(row1);
        const agentCell = addedRow.getCell("agent");
        const dayCell = addedRow.getCell("day");

        // Some formatting
        addedRow.alignment = {horizontal: "right", vertical: "middle"};
        addedRow.height = 25;
        agentCell.alignment = {horizontal: "center", vertical: "middle"};
        dayCell.alignment = {horizontal: "center", vertical: "middle"};

        // Formatting time column
        addedRow.getCell(2).numFmt = "MMM D, YYYY";
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], {type: "application/octet-stream"}),
        `cq-agent-table-${format_today_date_for_file()}.xlsx`,
    );
};

/**
 * Prepare the blob and file name for the call records excel file
 * to be downloaded
 * @param calls the call records to be downloaded
 * @param callQueueName the name of the call queue used for the file name
 * @param chattoUrl the url of the chatto server for putting the call links
 * @param intl the intl object for translations
 * @returns a tuple of the blob and the file name
 **/
export const prepare_call_records_for_excel = async (
    calls: {
        onBehalfOf?: string;
        remoteNumber: string;
        callTime: any;
        duration: number;
        recording?: {
            id: string;
            store: RecordingStore;
        };
    }[],
    callQueueName: string,
    chattoUrl: string,
    intl: IntlShape,
): Promise<[Blob, string]> => {
    const {Workbook} = await import(/* webpackChunkName: "exceljs" */ "exceljs");
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet("Call Records");

    sheet.columns = [
        {header: intl.formatMessage({id: "call_list.caller", defaultMessage: "Caller"}), key: "caller", width: 20},
        {header: intl.formatMessage({id: "call_list.agent", defaultMessage: "Agent"}), key: "agent", width: 40},
        {header: intl.formatMessage({id: "call_list.callTime", defaultMessage: "Time"}), key: "callTime", width: 40},
        {
            header: intl.formatMessage({id: "call_list.duration", defaultMessage: "Duration"}),
            key: "duration",
            width: 30,
        },
        {
            header: intl.formatMessage({id: "call_list.recording", defaultMessage: "Recording"}),
            key: "recording",
            width: 20,
        },
    ];

    // Setting header style
    const sheetHeader = sheet.getRow(1);
    sheetHeader.alignment = {horizontal: "center", vertical: "middle"};
    sheetHeader.height = 25;

    calls.forEach((call) => {
        const row: Record<string, any> = {};
        row["caller"] = call.onBehalfOf;
        row["agent"] = call.remoteNumber;

        row["callTime"] = new Date(call.callTime).toLocaleString();
        row["duration"] = formatDuration(call.duration * 1000, intl);

        // only show download link if recording is available and is stored internally
        // if stored externally, just show the id
        // otherwise do not show anything
        if (call.recording) {
            if (call.recording.store === RecordingStore.Internal) {
                row["recording"] = {
                    text: intl.formatMessage({id: "download", defaultMessage: "Download"}),
                    hyperlink: `${chattoUrl}download/${call.recording.id}`,
                    tooltip: call.recording.id,
                };
            } else {
                row["recording"] = call.recording.id;
            }
        }

        const addedRow = sheet.addRow(row);

        // Some formatting
        addedRow.alignment = {horizontal: "center", vertical: "middle"};
        addedRow.height = 25;

        // Formatting time column
        addedRow.getCell("callTime").numFmt = "MMM D, YYYY";
    });

    const buffer = await workbook.xlsx.writeBuffer();
    return [
        new Blob([buffer], {type: "application/octet-stream"}),
        `call-records-${callQueueName}-${format_today_date_for_file()}.xlsx`,
    ];
};
